import { Routes } from "@angular/router";
import { AuthGuard } from "./core/guard/auth.guard";
import { FuturesOrderDetailsPageGuard } from "./core/guard/futures-order-details-page-guard";
import { FuturesPaymentPageGuard } from "./core/guard/futures-payment-page-guard";
import { LoggedInAuthGuard } from "./core/guard/logged-in-auth.guard";
import { LanguageResolver } from "./core/resolvers/language-resolver";
import { MainComponent } from "./landing/shared/layout/main/main.component";
import { AuthLayoutComponent } from "./shared/app-layout/auth-layout/auth-layout.component";
import { MainLayoutComponent } from "./shared/app-layout/main-layout/main-layout.component";

const translateKey: string = "title.";

const mainAppRoutes: Routes = [
  {
    path: "",
    pathMatch: "full",
    redirectTo: "auth",
  },
  {
    path: "new-challenge",
    data: { title: `${translateKey}landingPage.title` },
    component: MainComponent,
    loadChildren: () => import("./landing/landing.routes").then((r) => r.landingRoutes),
  },
  {
    path: "upgrade-challenge",
    data: { title: `${translateKey}landingPage.title` },
    component: MainComponent,
    canActivate: [AuthGuard],
    loadChildren: () => import("./landing/landing.routes").then((r) => r.landingRoutes),
  },
  {
    path: "futures/new-challenge",
    data: { title: `${translateKey}landingPage.title` },
    component: MainComponent,
    children: [
      {
        path: "",
        pathMatch: "full",
        redirectTo: "get-funded",
      },
      {
        path: "get-funded",
        data: { title: `${translateKey}futures.newChallenge` },
        loadComponent: () =>
          import("./futures/new-challenge/futures-new-challenge.component").then(
            (c) => c.FuturesNewChallengeComponent,
          ),
      },
      {
        path: "order-details",
        data: { title: "order-details" },
        canActivate: [FuturesOrderDetailsPageGuard],
        loadComponent: () =>
          import("./futures/order-details/futures-order-details.component").then(
            (c) => c.FuturesOrderDetailsComponent,
          ),
      },
      {
        path: "payment",
        data: { title: "payment" },
        canActivate: [FuturesPaymentPageGuard],
        loadComponent: () =>
          import("./futures/payment/futures-payment.component").then(
            (c) => c.FuturesPaymentComponent,
          ),
      },
    ],
  },
  {
    path: "",
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: "dashboard",
        data: { title: `${translateKey}dashboard.title` },
        children: [
          {
            path: "",
            data: {
              breadcrumb: "app.dashboard.breadcrumb.dashboard",
              title: `${translateKey}dashboard.title`,
            },
            loadChildren: () =>
              import("./dashboard/dashboard.routes").then((r) => r.dashboardRoutes),
          },
        ],
      },
      {
        path: "accounts",
        data: {
          breadcrumb: "app.dashboard.breadcrumb.challenges",
          title: `${translateKey}challenges`,
        },
        loadComponent: () =>
          import("./dashboard/all-challenges/all-challenges.component").then(
            (c) => c.AllChallengesComponent,
          ),
      },
      {
        path: "payouts",
        children: [
          {
            path: "",
            data: { breadcrumb: "Payouts", title: `${translateKey}payouts` },
            loadChildren: () => import("./payouts/payouts.routes").then((r) => r.payoutsRoutes),
          },
        ],
      },
      {
        path: "contracts",
        children: [
          {
            path: "",
            data: { breadcrumb: "Contracts", title: `${translateKey}contracts` },
            loadChildren: () =>
              import("./contracts/contracts.routes").then((r) => r.contractsRoutes),
          },
        ],
      },
      {
        path: "profile",
        children: [
          {
            path: "",
            data: { breadcrumb: "Profile", title: `${translateKey}profile.title` },
            loadChildren: () => import("./profile/profile.routes").then((r) => r.profileRoutes),
          },
        ],
      },
      {
        path: "trading-academy",
        children: [
          {
            path: "",
            data: { breadcrumb: "Trading Academy", title: `${translateKey}tradingAcademy` },
            loadChildren: () =>
              import("./trading-academy/trading-academy.routes").then(
                (r) => r.tradingAcademyRoutes,
              ),
          },
        ],
      },
      {
        path: "billing",
        children: [
          {
            path: "",
            data: { breadcrumb: "Billing", title: `${translateKey}billing` },
            loadChildren: () => import("./billing/billing.routes").then((r) => r.billingRoutes),
          },
        ],
      },
      {
        path: "contact-us",
        children: [
          {
            path: "",
            data: { breadcrumb: "Contact us", title: `${translateKey}contactUs` },
            loadChildren: () =>
              import("./contact-us/contact-us.routes").then((r) => r.contactUsRoutes),
          },
        ],
      },
      {
        path: "download",
        children: [
          {
            path: "",
            data: { breadcrumb: "Download", title: `${translateKey}download` },
            loadChildren: () =>
              import("./downloads/downloads.routes").then((r) => r.downloadsRoutes),
          },
        ],
      },
      {
        path: "trade2earn",
        children: [
          {
            path: "",
            data: { title: `${translateKey}earn` },
            loadChildren: () => import("./earn/earn.routes").then((r) => r.earnRoutes),
          },
        ],
      },
      {
        path: "payment-status",
        children: [
          {
            path: "",
            data: { title: `${translateKey}paymentStatus` },
            loadChildren: () =>
              import("./payment-status/payment-status.routes").then((r) => r.paymentStatusRoutes),
          },
        ],
      },
      {
        path: "affiliate",
        children: [
          {
            path: "",
            data: { breadcrumb: "Affiliate Program", title: `${translateKey}affiliate` },
            loadChildren: () =>
              import("./affiliate/affiliate.routes").then((r) => r.affiliateRoutes),
          },
        ],
      },
      {
        path: "futures",
        children: [
          {
            path: "",
            data: { breadcrumb: "app.futures.breadcrumb", title: `${translateKey}futures.title` },
            loadChildren: () => import("./futures/futures.routes").then((r) => r.futuresRoutes),
          },
        ],
      },
    ],
  },
  {
    path: "auth",
    data: { title: `${translateKey}login` },
    component: AuthLayoutComponent,
    canActivate: [LoggedInAuthGuard],
    children: [
      {
        path: "",
        data: { title: `${translateKey}login` },
        loadChildren: () => import("./auth/auth.routes").then((r) => r.authRoutes),
      },
    ],
  },
  {
    path: "verify-email",
    component: AuthLayoutComponent,
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./auth/verify-email/verify-email.component").then((c) => c.VerifyEmailComponent),
      },
    ],
  },
  {
    path: "action",
    component: AuthLayoutComponent,
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./auth/action/action.component").then((c) => c.ActionComponent),
      },
    ],
  },
];

export const routes: Routes = [
  ...mainAppRoutes,
  {
    path: ":lang",
    resolve: { lang: LanguageResolver },
    children: [
      ...mainAppRoutes,
      {
        path: ":lang",
        pathMatch: "full",
        redirectTo: ":lang/dashboard",
      },
    ],
  },
];
